import maplibregl from 'maplibre-gl'
import { addOverlayLayer } from '../../components/map/layer-order-manager.js'
import { logger } from '../../utils/logger.js'
import { DEM_URL, DEM_TILE_SIZE, DEM_MAX_ZOOM } from './dem-config.js'

const log = logger.create('hillshade')
const SOURCE_ID = 'hillshade-dem'
const LAYER_ID = 'hillshade-layer'
const DEFAULT_OPACITY = 0.5

class HillshadeService {
  private map: maplibregl.Map | null = null
  private _enabled = false
  private _opacity = DEFAULT_OPACITY
  private cairnTiles: ((name: string, url: string) => string[]) | null = null

  get enabled(): boolean {
    return this._enabled
  }
  get opacity(): number {
    return this._opacity
  }

  register(map: maplibregl.Map, cairnTiles?: (name: string, url: string) => string[]): void {
    this.map = map
    this.cairnTiles = cairnTiles ?? null
  }

  unregister(): void {
    this.disable()
    this.map = null
    this.cairnTiles = null
  }

  private getTiles(): string[] {
    return this.cairnTiles ? this.cairnTiles('terrarium-dem', DEM_URL) : [DEM_URL]
  }

  private getPaint(): Record<string, unknown> {
    return {
      'hillshade-exaggeration': 0.3,
      'hillshade-shadow-color': `rgba(0, 0, 0, ${this._opacity})`,
      'hillshade-highlight-color': `rgba(255, 255, 255, ${this._opacity})`,
      'hillshade-illumination-direction': 315,
    }
  }

  enable(): void {
    if (!this.map || this._enabled) {
      return
    }
    this._enabled = true
    log.info('enabling hillshade')

    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'raster-dem',
        tiles: this.getTiles(),
        tileSize: DEM_TILE_SIZE,
        maxzoom: DEM_MAX_ZOOM,
        encoding: 'terrarium',
      })
    }

    if (!this.map.getLayer(LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LAYER_ID,
          type: 'hillshade',
          source: SOURCE_ID,
          paint: this.getPaint(),
        } as maplibregl.LayerSpecification,
        'hillshade'
      )
    }
  }

  disable(): void {
    if (!this.map) {
      return
    }
    this._enabled = false

    if (this.map.getLayer(LAYER_ID)) {
      this.map.removeLayer(LAYER_ID)
    }
    if (this.map.getSource(SOURCE_ID)) {
      this.map.removeSource(SOURCE_ID)
    }
  }

  toggle(): void {
    if (this._enabled) {
      this.disable()
    } else {
      this.enable()
    }
  }

  reAdd(): void {
    if (!this._enabled || !this.map) {
      return
    }
    if (!this.map.getSource(SOURCE_ID)) {
      this.map.addSource(SOURCE_ID, {
        type: 'raster-dem',
        tiles: this.getTiles(),
        tileSize: DEM_TILE_SIZE,
        maxzoom: DEM_MAX_ZOOM,
        encoding: 'terrarium',
      })
    }
    if (!this.map.getLayer(LAYER_ID)) {
      addOverlayLayer(
        this.map,
        {
          id: LAYER_ID,
          type: 'hillshade',
          source: SOURCE_ID,
          paint: this.getPaint(),
        } as maplibregl.LayerSpecification,
        'hillshade'
      )
    }
  }

  setOpacity(opacity: number): void {
    this._opacity = Math.max(0, Math.min(1, opacity))
    if (this.map?.getLayer(LAYER_ID)) {
      this.map.setPaintProperty(
        LAYER_ID,
        'hillshade-shadow-color',
        `rgba(0, 0, 0, ${this._opacity})`
      )
      this.map.setPaintProperty(
        LAYER_ID,
        'hillshade-highlight-color',
        `rgba(255, 255, 255, ${this._opacity})`
      )
    }
  }
}

export const hillshadeService = new HillshadeService()
